// Pure derivation of what the POS member panel / info pills / money strip
// can offer for an attached Club member: whether points and wallet balance
// are usable on the viewed order right now, and why not otherwise.
import { DiscountType } from '@/enums/DiscountType';
import { IClubProgramPublic } from '@/interfaces/IClubProgram';
import { roundPrice } from '@/utils';
import {
  clubProgramCoversBranch,
  maxRedeemablePoints,
  valueForPoints,
} from './club-program-math';

export type TClubPointsReason =
  | 'disabled'
  | 'noPoints'
  | 'belowMin'
  | 'noAmount'
  /** Something is due, but less than the program's minimum redemption. */
  | 'amountTooLow';
export type TClubBalanceReason = 'disabled' | 'noBalance' | 'noAmount';

export interface IClubPanelMember {
  pointsBalance: number;
  /** Unknown (still loading) when null: the balance tile shows a skeleton. */
  cashTotal: number | null;
}

export interface IClubPanelOrder {
  total: number;
  totalPaid?: number | null;
  roundDifference?: number | null;
  deliveryCost?: number | null;
  discounts?: Array<{ type: DiscountType; value: number | string }> | null;
}

export interface IClubMemberPanelState {
  points: {
    usable: boolean;
    /** Max points redeemable on this order right now (0 when not usable). */
    maxPoints: number;
    /** Money value of the member's whole points balance. */
    value: number;
    /** Points the member is short of the program minimum ('belowMin'). */
    missingPoints: number;
    reason: TClubPointsReason | null;
  };
  balance: {
    usable: boolean;
    /** Amount a one-tap "Usar saldo" would charge: min(cashTotal, due). */
    amount: number;
    reason: TClubBalanceReason | null;
  };
}

/**
 * Whether the panel's empty row ("Identificar socio") applies to an order
 * without a member: same gate as the draft top bar's Club button — the
 * program (active ones only reach the client) covers this branch. Attaching
 * alone earns, so no resource needs to be usable right now.
 */
export function isClubAttachAvailable(params: {
  program: IClubProgramPublic | null | undefined;
  branchId: number | null | undefined;
  order: { clubMember?: unknown | null };
}): boolean {
  const { program, branchId, order } = params;
  if (!program || !clubProgramCoversBranch(program, branchId)) return false;
  return order.clubMember == null;
}

export function getClubMemberPanelState(params: {
  program: IClubProgramPublic | null | undefined;
  branchId: number | null | undefined;
  member: IClubPanelMember;
  order: IClubPanelOrder;
}): IClubMemberPanelState {
  const { program, branchId, member, order } = params;
  const covers = !!program && clubProgramCoversBranch(program, branchId);
  const due = roundPrice((+order.total || 0) - (+(order.totalPaid ?? 0) || 0));

  return {
    points: derivePoints(program, covers, member, order, due),
    balance: deriveBalance(program, covers, member, due),
  };
}

function derivePoints(
  program: IClubProgramPublic | null | undefined,
  covers: boolean,
  member: IClubPanelMember,
  order: IClubPanelOrder,
  due: number
): IClubMemberPanelState['points'] {
  const redeemEnabled =
    !!program &&
    program.pointsEnabled &&
    program.redeemPointsCost > 0 &&
    program.redeemValue > 0 &&
    covers;
  // No money value when the program cannot redeem points here: a rate may
  // still be configured, but showing "$400" for unredeemable points misleads.
  if (!program || !redeemEnabled) {
    return {
      maxPoints: 0,
      value: 0,
      missingPoints: 0,
      usable: false,
      reason: 'disabled',
    };
  }
  const base = {
    maxPoints: 0,
    value: valueForPoints(program, member.pointsBalance),
    missingPoints: 0,
  };

  if (member.pointsBalance <= 0) {
    return { ...base, usable: false, reason: 'noPoints' };
  }
  const minPoints = Math.max(program.minRedeemPoints, 1);
  if (member.pointsBalance < minPoints) {
    return {
      ...base,
      usable: false,
      missingPoints: minPoints - member.pointsBalance,
      reason: 'belowMin',
    };
  }

  if (due <= 0) {
    return { ...base, usable: false, reason: 'noAmount' };
  }

  const existingClubValue = roundPrice(
    (order.discounts ?? [])
      .filter((discount) => discount.type === DiscountType.Club)
      .reduce((acc, discount) => acc + +discount.value, 0)
  );
  const maxPoints = maxRedeemablePoints({
    program,
    orderTotal: +order.total,
    roundDifference: +(order.roundDifference ?? 0),
    deliveryCost: +(order.deliveryCost ?? 0),
    totalPaid: +(order.totalPaid ?? 0),
    existingClubValue,
    memberPoints: member.pointsBalance,
  });
  if (maxPoints < minPoints) {
    return { ...base, usable: false, reason: 'amountTooLow' };
  }
  return { ...base, maxPoints, usable: true, reason: null };
}

function deriveBalance(
  program: IClubProgramPublic | null | undefined,
  covers: boolean,
  member: IClubPanelMember,
  due: number
): IClubMemberPanelState['balance'] {
  if (!program?.balanceEnabled || !covers) {
    return { usable: false, amount: 0, reason: 'disabled' };
  }
  const cashTotal = member.cashTotal ?? 0;
  if (cashTotal <= 0) {
    return { usable: false, amount: 0, reason: 'noBalance' };
  }
  if (due <= 0) {
    return { usable: false, amount: 0, reason: 'noAmount' };
  }
  return {
    usable: true,
    amount: roundPrice(Math.min(cashTotal, due)),
    reason: null,
  };
}
